import CheckIsLoggedInGuard from 'app/core/guards/CheckIsLoggedInGuard';
import HasToBeAgencyGuard from 'app/core/guards/HasToBeAgencyGuard';
import HasToBeAgencyOnboardingStep1Guard from 'app/core/guards/HasToBeAgencyOnboardingStep1Guard';
import HasToBeAgencyOnboardingStep3Guard from 'app/core/guards/HasToBeAgencyOnboardingStep3Guard';
import HasToBeLoggedInGuard from 'app/core/guards/HasToBeLoggedInGuard';
import HasToPassOnboarding from 'app/core/guards/HasToPassOnboarding';
import React from 'react';
import { Redirect } from 'react-router';
import { RouteConfig } from 'react-router-config';

import { CannotBeLoggedInGuard } from '../../guards/CannotBeLoggedInGuard';
import NoSidebar from '../../layout/NoSidebar';

const PREFIX = '/agency/';

const agencyRoutes: Array<RouteConfig> = [
  {
    path: PREFIX + 'onboarding/claim-account',
    component: React.lazy(
      () =>
        import('../../../main/agency/onboarding/step1/AgencyOnboardingStep1')
    ),
    canActivate: [
      HasToBeLoggedInGuard,
      HasToBeAgencyGuard,
      HasToBeAgencyOnboardingStep1Guard,
    ],
    layout: NoSidebar,
  },
  {
    path: PREFIX + 'onboarding/step1',
    component: React.lazy(
      () =>
        import('../../../main/agency/onboarding/step1/AgencyOnboardingStep1')
    ),
    canActivate: [CannotBeLoggedInGuard],
    layout: NoSidebar,
  },
  {
    path: PREFIX + 'onboarding/step2',
    component: React.lazy(
      () =>
        import('../../../main/agency/onboarding/step3/AgencyOnboardingStep3')
    ),
    canActivate: [
      HasToBeLoggedInGuard,
      HasToBeAgencyGuard,
      HasToBeAgencyOnboardingStep3Guard,
    ],
    layout: NoSidebar,
  },
  {
    path: PREFIX + 'dashboard',
    component: React.lazy(
      () => import('../../../main/agency/dashboard/AgencyDashboard')
    ),
    canActivate: [
      HasToBeLoggedInGuard,
      HasToBeAgencyGuard,
      HasToPassOnboarding,
    ],
  },
  {
    path: PREFIX + 'profile',
    exact: true,
    component: () => <Redirect to={PREFIX + 'profile/main'} />,
  },
  {
    path: PREFIX + 'account',
    component: React.lazy(
      () => import('../../../main/agency/account/AccountProfilePage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'profile/:id',
    component: React.lazy(
      () => import('../../../main/agency/profile/AgencyProfilePage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'company/invite',
    component: React.lazy(
      () => import('../../../main/agency/company/add-user/AddUser')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'company/edit-invitation',
    component: React.lazy(
      () => import('../../../main/agency/company/add-user/AddUser')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'company/employees/:id/:tabId',
    component: React.lazy(
      () =>
        import(
          '../../../main/agency/profile/users-sections/employee/EmployeePage'
        )
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'public/:id',
    exact: true,
    component: React.lazy(
      () => import('../../../main/agency/public-profile/PublicProfile')
    ),
    canActivate: [CheckIsLoggedInGuard],
  },

  // BOOKINGS SECTION
  {
    path: PREFIX + 'bookings/:sectionId/invoices/:invoiceId',
    component: React.lazy(
      () =>
        import('../../../main/agency/bookings/details/AgencyInvoiceDetailsPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'payouts/:payoutId',
    component: React.lazy(
      () =>
        import('../../../main/agency/bookings/details/AgencyPayoutDetailsPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/:sectionId/invoices/:invoiceId/receipt',
    component: React.lazy(
      () =>
        import('../../../main/agency/bookings/details/AgencyReceiptDetailsPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/confirmed-jobs/create-invoice/:leadId',
    component: React.lazy(
      () =>
        import(
          '../../../main/agency/bookings/details/AgencyBookingCreateInvoicePage'
        )
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/:sectionId/details/:leadId',
    component: React.lazy(
      () =>
        import('../../../main/agency/bookings/details/AgencyBookingDetailsPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: '/agency/bookings/:sectionId/proposal/:leadId/show',
    component: React.lazy(
      () =>
        import('../../../main/agency/bookings/proposal/AgencyProposalViewPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/:sectionId/proposal/:leadId',
    component: React.lazy(
      () => import('../../../main/agency/bookings/proposal/AgencyProposalPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/:sectionId/schedule-fixed/:leadId',
    component: React.lazy(
      () =>
        import('../../../main/agency/bookings/proposal/AgencyScheduleFixedPage')
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/:sectionId/report/:reportId',
    component: React.lazy(
      () =>
        import(
          '../../../main/agency/bookings/report-details/AgencyReportDetailsPage'
        )
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/:sectionId/report/:reportId/receipt',
    component: React.lazy(
      () =>
        import(
          '../../../main/agency/bookings/details/EventReportReceiptDetailsPage'
        )
    ),
    exact: true,
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  {
    path: PREFIX + 'bookings/:sectionId?',
    component: React.lazy(
      () => import('../../../main/agency/bookings/AgencyBookingsPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
  // END BOOKINGS SECTION

  // INSPECTIONS SECTION
  {
    path: PREFIX + 'inspections/:sectionId',
    component: React.lazy(
      () => import('../../../main/agency/inspections/InspectionsListingSection')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },

  // MESSAGES SECTION
  {
    path: '/ami',
    exact: true,
    component: () => <Redirect to={'/agency/messaging/inbox'} />,
  },
  {
    path: '/ami/:id',
    exact: true,
    component: (props: any) => (
      <Redirect to={`/agency/messaging/inbox/${props.match.params.id}`} />
    ),
  },
  {
    path: PREFIX + 'messaging',
    exact: true,
    component: () => <Redirect to={PREFIX + 'messaging/inbox'} />,
  },
  {
    path: [
      PREFIX + 'messaging/create-thread/:recipientId',
      PREFIX + 'messaging/create-thread/:recipientId/:jobId',
    ],
    component: React.lazy(
      () => import('../../../main/agency/messaging/AgencyCreateThreadPage')
    ),
    exact: true,
    canActivate: [
      HasToBeLoggedInGuard,
      HasToBeAgencyGuard,
      HasToPassOnboarding,
    ],
  },
  {
    path: PREFIX + 'messaging/:sectionId',
    component: React.lazy(
      () => import('../../../main/agency/messaging/AgencyMessagingPage')
    ),
    exact: true,
    canActivate: [
      HasToBeLoggedInGuard,
      HasToBeAgencyGuard,
      HasToPassOnboarding,
    ],
  },
  {
    path: PREFIX + 'messaging/:sectionId/:threadId',
    component: React.lazy(
      () => import('../../../main/agency/messaging/AgencyMessagingPage')
    ),
    exact: true,
    canActivate: [
      HasToBeLoggedInGuard,
      HasToBeAgencyGuard,
      HasToPassOnboarding,
    ],
  },
  {
    path: PREFIX + 'notifications/:messageId',
    component: React.lazy(
      () =>
        import('../../../main/agency/messaging/AgencyNotificationLandingPage')
    ),
    exact: true,
    canActivate: [
      HasToBeLoggedInGuard,
      HasToBeAgencyGuard,
      HasToPassOnboarding,
    ],
  },

  // SETTINGS SECTION
  {
    path: PREFIX + 'settings',
    exact: true,
    component: () => <Redirect to={PREFIX + 'settings/general'} />,
  },
  {
    path: PREFIX + 'settings/:sectionId',
    component: React.lazy(
      () => import('../../../main/agency/settings/AgencySettingsPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },

  // PAYMENTS
  {
    path: PREFIX + 'payments',
    exact: true,
    component: () => <Redirect to={PREFIX + 'payments/payments-list'} />,
  },
  {
    path: PREFIX + 'payments/:id',
    component: React.lazy(
      () => import('../../../main/agency/payments/AgencyPaymentsPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },

  // REVIEWS
  {
    path: '/agency/reviews',
    exact: true,
    component: React.lazy(
      () => import('../../../main/agency/reviews/AgencyReviewsPage')
    ),
    canActivate: [HasToBeLoggedInGuard, HasToBeAgencyGuard],
  },
];

export default agencyRoutes;
